/**
 * The Translation framework's page side (docs/ops-protocol.md, "Translation
 * and the on-device language model"): `translate` host requests, answered
 * with the browser's built-in `Translator` (and `LanguageDetector` for an
 * unnamed source language) where the browser has them — Chrome 138 and later
 * on desktop. Without them every pair is unsupported.
 *
 * - `availability` → `{ status: "installed" | "supported" | "unsupported" }`
 *   (`available` / `downloadable` or `downloading` / `unavailable`).
 * - `translate` → `{ source, texts }`, or rejects with `unsupported: …`,
 *   `unidentified: …` (no source language could be detected) or
 *   `download: …` (the pair's model needs a download the session may not ask
 *   for). A translator is made once per pair and kept.
 */

import type { TranslateRequestParams } from './protocol';

export type TranslationAvailability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

/** A translator for one pair (Chrome's `Translator` instance). */
export interface TranslatorLike {
  translate(text: string): Promise<string>;
}

/** The browser's translation facilities; tests substitute them. */
export interface TranslationHost {
  availability(source: string, target: string): Promise<TranslationAvailability>;
  create(source: string, target: string): Promise<TranslatorLike>;
  /** The most likely language of the text (BCP 47), or null. Absent without a detector. */
  detect?(text: string): Promise<string | null>;
}

export type TranslateResult = {
  status?: 'installed' | 'supported' | 'unsupported';
  source?: string;
  texts?: string[];
};

const STATUS: Record<TranslationAvailability, 'installed' | 'supported' | 'unsupported'> = {
  available: 'installed',
  downloadable: 'supported',
  downloading: 'supported',
  unavailable: 'unsupported',
};

/** Translators made so far, per `source>target`. */
const translators = new Map<string, Promise<TranslatorLike>>();

async function sourceLanguage(params: TranslateRequestParams, host: TranslationHost, text: string): Promise<string> {
  if (params.source) return params.source;
  const detected = host.detect && text.trim() ? await host.detect(text) : null;
  if (!detected || detected === 'und') throw new Error('unidentified: the language of the text could not be detected');
  return detected;
}

export async function performTranslate(
  params: TranslateRequestParams,
  host: TranslationHost | undefined,
): Promise<TranslateResult> {
  const target = params.target;
  if (params.action === 'availability') {
    if (!host) return { status: 'unsupported' };
    const source = await sourceLanguage(params, host, params.text ?? '');
    if (source === target) return { status: 'installed' };
    return { status: STATUS[await host.availability(source, target)] ?? 'unsupported' };
  }
  if (params.action !== 'translate') {
    throw new Error(`unsupported: translate action ${String((params as { action: unknown }).action)}`);
  }
  if (!host) throw new Error('unsupported: this browser has no built-in translator');
  const texts = params.texts ?? [];
  const source = await sourceLanguage(params, host, texts.join('\n'));
  if (source === target) return { source, texts };
  const key = `${source}>${target}`;
  let translator = translators.get(key);
  if (!translator) {
    const availability = await host.availability(source, target);
    if (availability === 'unavailable') throw new Error(`unsupported: no translator from ${source} to ${target}`);
    if (availability !== 'available' && params.allowDownload !== true) {
      throw new Error(`download: the ${source} to ${target} model is not installed`);
    }
    translator = host.create(source, target);
    translators.set(key, translator);
    translator.catch(() => translators.delete(key));
  }
  const ready = await translator;
  const translated = await Promise.all(texts.map((text) => (text ? ready.translate(text) : Promise.resolve(''))));
  return { source, texts: translated };
}

/** Forgets the translators made so far (tests). */
export function resetTranslators(): void {
  translators.clear();
}

interface TranslatorGlobal {
  availability(options: { sourceLanguage: string; targetLanguage: string }): Promise<TranslationAvailability>;
  create(options: { sourceLanguage: string; targetLanguage: string }): Promise<TranslatorLike>;
}

interface LanguageDetectorGlobal {
  create(): Promise<{ detect(text: string): Promise<{ detectedLanguage: string; confidence: number }[]> }>;
}

/** Chrome's `Translator` and `LanguageDetector`, where the page has them. */
export function browserTranslationHost(): TranslationHost | undefined {
  const scope = globalThis as unknown as { Translator?: TranslatorGlobal; LanguageDetector?: LanguageDetectorGlobal };
  const translator = scope.Translator;
  if (!translator || typeof translator.create !== 'function') return undefined;
  const detectorGlobal = scope.LanguageDetector;
  let detector: ReturnType<LanguageDetectorGlobal['create']> | undefined;
  return {
    availability: (sourceLanguage, targetLanguage) => translator.availability({ sourceLanguage, targetLanguage }),
    create: (sourceLanguage, targetLanguage) => translator.create({ sourceLanguage, targetLanguage }),
    ...(detectorGlobal && typeof detectorGlobal.create === 'function'
      ? {
          detect: async (text: string) => {
            detector ??= detectorGlobal.create();
            const results = await (await detector).detect(text);
            const best = results[0];
            return best && best.confidence >= 0.5 ? best.detectedLanguage : null;
          },
        }
      : {}),
  };
}
